import { type ProductTaxonomyType } from '@carrefour-gcs/shared';
import { useQuery } from '@tanstack/vue-query';
import { storeToRefs } from 'pinia';
import { computed, unref, type Ref } from 'vue';

import { useKpisPerimeter, type FiltersTreeType } from '#composables/kpisPerimeter';
import { filterToTreeViewItemWithDefaultValue } from '#domain/core/filter/filter.mapper';
import { findBrands } from '#domain/core/filter/filter.query';
import { transformFilterFromSearch } from '#domain/core/filter/filter.utilities';
import { useUserStore } from '#stores/user.store';

interface UseBrandDataProps {
  debouncedSearch: Readonly<Ref<string>>;
  enabled: Ref<boolean>;
  checkedItemsState: Ref<FiltersTreeType>;
  taxonomy: Ref<ProductTaxonomyType>;
}

export function useBrandData({ debouncedSearch, enabled, checkedItemsState, taxonomy }: UseBrandDataProps) {
  const {
    filters: { selectedFiltersTree }
  } = useKpisPerimeter();
  const user = useUserStore();
  const { isAnonymized } = storeToRefs(user);

  const currentHoldingFilters = computed(() => {
    return [];
  });

  const currentLocalSupplierFilters = computed(() => {
    return [];
  });

  const currentBrandFilters = computed(() => {
    return Object.entries(selectedFiltersTree.value.brandsFilters)
      .filter(([, state]) => state.state === 'checked' && state.filterType === 'brand')
      .map(([filter]) => filter);
  });

  const {
    data: brandData,
    isLoading,
    isFetching
  } = useQuery({
    enabled,
    queryKey: [
      findBrands.name,
      debouncedSearch,
      currentHoldingFilters,
      currentLocalSupplierFilters,
      currentBrandFilters,
      taxonomy
    ],
    queryFn: async () => {
      return findBrands({
        orNodeIdIn: currentBrandFilters.value,
        search: debouncedSearch.value,
        limit: 100,
        taxonomy: unref(taxonomy)
      });
    },
    select: (result) => {
      if (result.status === 'error') {
        return undefined;
      }

      return result.data;
    }
  });

  const itemsBrands = computed(() => {
    if (!brandData.value || !enabled.value) {
      return undefined;
    }

    return filterToTreeViewItemWithDefaultValue({
      filter: isAnonymized.value
        ? transformFilterFromSearch(brandData.value, 'Brand {level} - ******')
        : brandData.value,
      defaultFilters: isAnonymized.value
        ? transformFilterFromSearch(brandData.value, 'Brand {level} - ******')
        : brandData.value,
      selectedFilters: checkedItemsState.value.brandsFilters
    });
  });

  const isLoadingBrands = computed(() => {
    // on tanstack query v4, there is a [known issue](https://github.com/TanStack/query/issues/3975) that
    // isLoading and isStale stay true if `enabled` is false.
    return isLoading.value && isFetching.value;
  });

  return { itemsBrands, isLoadingBrands };
}
